import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Box, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { T } from "react-translator-component";
import appServices from "../../services/app-services/appServices";
import GCBackButton from "../../components/GreenCity/GCBackButton";
import EventCalendar from "../../components/GreenCity/EventCalender";
import LoadingModal from "../../components/LoadingModal";
import TextButton from "../../components/GreenCity/TextButton";
import PostCard from "../../components/lists/PostCard";
import LoadMoreButton from "../../components/LoadMoreButton";
import { styles as oldstyles } from "../styles";
import { useAppDispatch } from "../../redux/store";
import { createNewPostState } from "../../util/CreatePost"
import { isGroupLeader, isSubLeaderRole } from "../../util/utils";

const AddIcon = () => {
  return (
    <div style={styles.addIcon}>
      <AddRoundedIcon sx={{ width: 20, height: 20 }} />
    </div>
  );
};

const GroupEventsCalendar = () => {
  const defaultImage = require("../../assets/images/default.png");

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const groupInfo = useSelector((state: any) => state.gcGroup);
  const { user } = useSelector((state: any) => state.userAuth);

  const [posts, setPosts] = useState<any[]>([]);
  const [dates, setDates] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState<any>({});
  const [groupIconList, setGroupIconList] = useState<any[]>([]);

  const getEvents = async (page: number) => {
    if (!page) return

    try {
      setLoading(true);

      const response = await appServices.getGroupEventsPagination(
        groupInfo.id,
        page
      );

      const newItems = [...posts, ...response.posts];
      setPosts(newItems);

      const datesArray = newItems
        .filter((post: { status: string }) => post.status === "Approved") // Filter out cancelled posts
        .flatMap((post: { post_events: any[] }) =>
          post.post_events.map((event) => ({
            title: event.title,
            start: event.start_date,
            end: dayjs(event.end_date).add(1, "day").format("YYYY-MM-DD"),
          }))
        );
      setDates(datesArray);
      setPagination(response.pagination);
    } catch {
      // pass
    } finally {
      setLoading(false);
    }
  };

  const getSelectedEvent = (date: any) => {
    const filteredPosts = posts.filter((post: any) =>
      post.post_events.some((event: any) => {
        return (
          event.start_date === date.start &&
          date.end === dayjs(event.end_date).add(1, "day").format("YYYY-MM-DD")
        );
      })
    );

    navigate("/calenderevent", { state: { event: filteredPosts } });
  };

  const getGroupIconImages = async () => {
    if (groupInfo) {
      const imageInfo: {groupId: number, icon: any} = {groupId: groupInfo.id, icon: groupInfo.groupImage}

      await appServices.getGroupIcon(groupInfo.id).then((blob: any) => {
        const file = new File([blob], groupInfo.name, { type: blob.type });
        imageInfo.icon = file
      }).catch(() => {
        console.error("Failed to get group icon image")
      });

      setGroupIconList((prevImages: any) => [...prevImages, imageInfo])
    }
  }

  const fetchGroupIcon = (groupId: number) => {
    return groupIconList.find((image: any) => image.groupId === groupId)?.icon
  }

  const displayGroupIcon = () => {
    const groupIcon = fetchGroupIcon(groupInfo?.id)
    return groupIcon instanceof File ? URL.createObjectURL(groupIcon) : defaultImage
  }

  useEffect(() => {
    getEvents(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getGroupIconImages()
  }, [groupInfo])

  return (
    <Box>
      <div
        style={{
          paddingTop: 100,
          paddingLeft: 16,
          paddingBottom: "12px",
          boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.3)",
        }}
      >
        <LoadingModal visible={loading} handleClose={() => {}} />

        <GCBackButton onClick={() => navigate(-1)} />
        <Box component="div" style={styles.topButtonContainer}>
          <Box style={styles.eventPostingBox}>
            <Typography style={{ fontWeight: "700" }}>
              <img
                src={displayGroupIcon()}
                alt={"user"}
                style={{
                  ...oldstyles.farmProfileIcon,
                  height: 50,
                  width: 50,
                  margin: 0,
                }}
              />
              &nbsp;{groupInfo?.groupName}
            </Typography>
          </Box>
          {(isSubLeaderRole(groupInfo?.role) ||
            isGroupLeader(groupInfo, user)) && (
            <TextButton
              onClick={() =>
                navigate("/createPost", createNewPostState("GroupEventPost", user, groupInfo, [], dispatch))
              }
              Icon={AddIcon}
              title={T("submit_post")}
            />
          )}
        </Box>
      </div>
      <Box>
        <EventCalendar getSelectedEvent={getSelectedEvent} dates={dates} />
      </Box>
      <Box>
        {posts?.map((event: any) => (
          <Box key={event.id}>
            <PostCard
              post={event}
              showEventStatus
            />
          </Box>
        ))}
      </Box>

      <LoadMoreButton show={pagination?.next_page > 0} onClick={() => getEvents(pagination?.next_page)} />
    </Box>
  );
};

const styles = {
  eventPostingIcon: {
    background: "#FF3B00",
    borderRadius: "50%",
    padding: "3px 6px 3px 6px",
    color: "white",
  },
  eventPostingBox: {
    display: "flex",
    gap: "15px",
    alignItmes: "center",
    paddingTop: "10px",
    paddingLeft: "10px",
  },
  addIcon: {
    border: "2px solid",
    borderRadius: 5,
    borderColor: "#000000",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  topButtonContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 10,
    padding: "0 10px",
  },
};

export default GroupEventsCalendar;
